<template>
  <div
    class="grid gap-4 p-4"
    element-loading-background="rgba(24, 33, 48, 0.8)"
  >
    <div class="flex flex-col space-y-1.5 text-center sm:text-left">
      <h2 class="text-lg font-semibold leading-none tracking-tight">
        {{ title }}
      </h2>
      <p class="text-sm text-muted-foreground">
        Send test message to recipient
      </p>
    </div>
    <el-form
      class="flex flex-col"
      label-position="top"
      ref="ruleFormRef"
      :model="ruleForm"
      :rules="rules"
    >
      <el-form-item label="Channel" prop="channel">
        <SmartSelect
          v-model="ruleForm.channel"
          :options="item.channels"
          value-field="recipient_channel_id"
          display-field="channel"
          value-type="object"
          style="width: 100%"
        />
        <span>
          <template v-if="ruleForm.channel && ruleForm.channel.config">
            <span>{{ computedDisplayChannel }}</span>
          </template>
        </span>
      </el-form-item>
      <el-form-item
        v-if="showVoiceAsset"
        label="Voice Asset"
        prop="voice_asset_id"
      >
        <UploadAudioWidget
          v-model="ruleForm.voice_asset_id"
          :label="'Select a voice asset for voice call message'"
        />
      </el-form-item>
      <el-form-item v-if="showMessageInput" label="Message" prop="message">
        <el-input
          v-model="ruleForm.message"
          type="textarea"
          placeholder="Type your message here"
        />
      </el-form-item>
    </el-form>
    <div class="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
      <el-button @click="$emit('cancel')"> Cancel </el-button>
      <el-button
        :loading="submitting"
        type="primary"
        icon="el-icon-s-promotion"
        @click="handleSubmit"
      >
        Send Test Message
      </el-button>
    </div>
    <button
      @click="$emit('cancel')"
      type="button"
      class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
    >
      <i class="icon-x h-4 w-4" />
      <span class="sr-only">Close</span>
    </button>
  </div>
</template>
<script>
import {
  testNotificationRecipient,
  NotificationRecipientChannelTypes,
} from "@/api";
import SmartSelect from "@/components/SmartControls/SmartSelect.vue";
import UploadAudioWidget from "./UploadAudioWidget.vue";

export default {
  name: "SendMessageToRecipient",
  components: { SmartSelect, UploadAudioWidget },
  props: {
    item: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: "Send Test Message",
    },
  },
  data() {
    return {
      ruleForm: {
        channel: null,
        message: `Test message sent at ${new Date().toLocaleString()}`,
        voice_asset_id: undefined,
      },
      submitting: false,
    };
  },
  computed: {
    selectedChannelType() {
      return this.ruleForm.channel?.channel || null;
    },
    showVoiceAsset() {
      return (
        this.selectedChannelType ===
        NotificationRecipientChannelTypes.VOICE_CALL
      );
    },
    showMessageInput() {
      return !this.showVoiceAsset;
    },
    rules() {
      return {
        channel: [
          {
            required: true,
            message: "Please select a channel",
            trigger: "change",
          },
        ],
        message: [
          {
            validator: (rule, value, callback) => {
              if (!this.showMessageInput) {
                return callback();
              }
              if (!value || !String(value).trim()) {
                return callback(new Error("Please enter a message"));
              }
              return callback();
            },
            trigger: "blur",
          },
        ],
        voice_asset_id: [
          {
            validator: (rule, value, callback) => {
              if (!this.showVoiceAsset) {
                return callback();
              }
              if (!value) {
                return callback(
                  new Error("Please upload a voice asset before sending")
                );
              }
              return callback();
            },
            trigger: "change",
          },
        ],
      };
    },
    computedDisplayChannel() {
      if (!this.ruleForm.channel) return "";
      const channel = this.ruleForm.channel;
      const keyvalue = Object.entries(channel.config)
        .map(([key, value]) => `${key}:${value}`)
        .join(", ");
      return keyvalue;
    },
  },
  watch: {
    "ruleForm.channel"() {
      if (!this.ruleForm.channel) {
        this.ruleForm.message = "";
        this.ruleForm.voice_asset_id = undefined;
      } else if (this.showVoiceAsset) {
        this.ruleForm.message = "";
      } else {
        this.ruleForm.voice_asset_id = undefined;
      }
      this.$nextTick(() => {
        this.$refs.ruleFormRef?.clearValidate?.([
          "message",
          "voice_asset_id",
        ]);
      });
    },
  },
  methods: {
    async handleSubmit() {
      await this.$refs.ruleFormRef.validate();
      this.submitting = true;
      try {
        const payload = {
          recipient_channel_id: this.ruleForm.channel.recipient_channel_id,
        };
        if (this.showMessageInput) {
          payload.message = this.ruleForm.message;
        }
        if (this.showVoiceAsset) {
          payload.voice_asset_id = this.ruleForm.voice_asset_id;
        }
        await testNotificationRecipient(payload);
        this.$emit("success");
        this.$message.success(
          `Test message sent successfully at ${new Date().toLocaleString()}`
        );
      } catch (error) {
        console.error("Error sending test message:", error);
        this.$message.error(
          `Failed to send test message at ${new Date().toLocaleString()}`
        );
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
